import React, { useState, useEffect } from 'react';
import DateRangePicker from './DateRangePicker';
import { admin } from '../../services/api';
import '../../styles/PayrollExport.css';

function PayrollExport() {
  const [dateRange, setDateRange] = useState({
    startDate: null,
    endDate: null
  });
  const [paymentMethod, setPaymentMethod] = useState('per_medication');
  const [rate, setRate] = useState('25.00');
  const [format, setFormat] = useState('csv');
  const [loading, setLoading] = useState(false);
  
  // New state for employee selection
  const [employeeSelection, setEmployeeSelection] = useState('all'); // 'all' or 'individual'
  const [employees, setEmployees] = useState([]);
  const [selectedEmployee, setSelectedEmployee] = useState(null);
  const [useCustomRate, setUseCustomRate] = useState(false);
  
  // QuickBooks status
  const [qbStatus, setQbStatus] = useState({ connected: false, loading: true });

  useEffect(() => {
    fetchEmployees();
    fetchQuickBooksStatus();
  }, []);

  const fetchEmployees = async () => {
    try {
      const response = await admin.getUsers({ role: 'pharmacist' });
      setEmployees(response.data || []);
    } catch (err) {
      console.error('Failed to fetch employees:', err);
    }
  };

  const fetchQuickBooksStatus = async () => {
    try {
      const response = await fetch('/api/quickbooks/status', {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });
      const data = await response.json();
      setQbStatus({ ...data, loading: false });
    } catch (err) {
      console.error('Failed to fetch QuickBooks status:', err);
      setQbStatus({ connected: false, loading: false });
    }
  };

  const handleDateChange = ({ startDate, endDate }) => {
    setDateRange({ startDate, endDate });
  };

  const handleEmployeeChange = (employeeId) => {
    const employee = employees.find(e => e.id === parseInt(employeeId));
    setSelectedEmployee(employee);
    
    if (employee && !useCustomRate) {
      // Auto-fill rate from employee's default
      const employeeRate = (employee.default_pay_rate || 0) / 100;
      setRate(employeeRate.toFixed(2));
      setPaymentMethod(employee.default_pay_method || 'per_medication');
    }
  };

  const handleCustomRateToggle = (checked) => {
    setUseCustomRate(checked);
    
    if (!checked && selectedEmployee) {
      // Restore employee's default rate
      const employeeRate = (selectedEmployee.default_pay_rate || 0) / 100;
      setRate(employeeRate.toFixed(2));
      setPaymentMethod(selectedEmployee.default_pay_method || 'per_medication');
    }
  };

  const handleExport = async () => {
    if (!dateRange.startDate || !dateRange.endDate) {
      alert('Please select a date range first');
      return;
    }

    const rateNum = parseFloat(rate);
    if (isNaN(rateNum) || rateNum <= 0) {
      alert('Please enter a valid rate');
      return;
    }

    setLoading(true);
    try {
      const response = await admin.exportPayroll({
        start_date: dateRange.startDate,
        end_date: dateRange.endDate,
        payment_method: paymentMethod,
        rate: rateNum,
        format: format
      });

      // Create download link
      const blob = new Blob([response.data], {
        type: format === 'csv' ? 'text/csv' : 'text/plain'
      });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `payroll_${dateRange.startDate}_${dateRange.endDate}.${format}`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);

      alert('Payroll report generated successfully!');
    } catch (err) {
      console.error('Export failed:', err);
      alert('Export failed: ' + (err.response?.data?.detail || err.message));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="payroll-export-section">
      <div className="payroll-header">
        <h3>💰 Payroll Export</h3>
        {!qbStatus.loading && (
          <div className={`qb-status ${qbStatus.connected ? 'connected' : 'disconnected'}`}>
            <span className="status-icon">{qbStatus.connected ? '✓' : '○'}</span>
            <span>QuickBooks {qbStatus.connected ? 'Connected' : 'Not Connected'}</span>
          </div>
        )}
      </div>
      
      <div className="payroll-form">
        <div className="form-section">
          <h4>Select Employee</h4>
          <div className="radio-group">
            <label className="radio-label">
              <input
                type="radio"
                name="employee_selection"
                value="all"
                checked={employeeSelection === 'all'}
                onChange={(e) => {
                  setEmployeeSelection(e.target.value);
                  setSelectedEmployee(null);
                }}
              />
              All Employees
            </label>
            <label className="radio-label">
              <input
                type="radio"
                name="employee_selection"
                value="individual"
                checked={employeeSelection === 'individual'}
                onChange={(e) => setEmployeeSelection(e.target.value)}
              />
              Individual Employee
            </label>
          </div>
          
          {employeeSelection === 'individual' && (
            <div className="employee-select-container">
              <select
                className="employee-select"
                value={selectedEmployee?.id || ''}
                onChange={(e) => handleEmployeeChange(e.target.value)}
              >
                <option value="">-- Select Employee --</option>
                {employees.map(emp => (
                  <option key={emp.id} value={emp.id}>
                    {emp.full_name} ({emp.credentials || 'No credentials'})
                  </option>
                ))}
              </select>
              
              {selectedEmployee && (
                <div className="employee-rate-info">
                  <p>
                    <strong>Default Rate:</strong> ${(selectedEmployee.default_pay_rate / 100).toFixed(2)}
                    ({selectedEmployee.default_pay_method === 'per_medication' ? 'per medication' : 'hourly'})
                  </p>
                  <label className="checkbox-label">
                    <input
                      type="checkbox"
                      checked={useCustomRate}
                      onChange={(e) => handleCustomRateToggle(e.target.checked)}
                    />
                    <span>Use custom rate</span>
                  </label>
                </div>
              )}
            </div>
          )}
        </div>
        
        <div className="form-section">
          <h4>Date Range</h4>
          <DateRangePicker onDateChange={handleDateChange} />
        </div>

        <div className="form-section">
          <h4>Payment Method</h4>
          <div className="radio-group">
            <label className="radio-label">
              <input
                type="radio"
                name="payment_method"
                value="per_medication"
                checked={paymentMethod === 'per_medication'}
                onChange={(e) => setPaymentMethod(e.target.value)}
                disabled={employeeSelection === 'individual' && selectedEmployee && !useCustomRate}
              />
              Per Medication ($ per medication reviewed)
            </label>
            <label className="radio-label">
              <input
                type="radio"
                name="payment_method"
                value="hourly"
                checked={paymentMethod === 'hourly'}
                onChange={(e) => setPaymentMethod(e.target.value)}
                disabled={employeeSelection === 'individual' && selectedEmployee && !useCustomRate}
              />
              Hourly ($ per hour worked)
            </label>
          </div>
        </div>

        <div className="form-section">
          <h4>Rate</h4>
          <div className="rate-input-wrapper">
            <span className="currency-symbol">$</span>
            <input
              type="number"
              step="0.01"
              min="0"
              value={rate}
              onChange={(e) => setRate(e.target.value)}
              placeholder="25.00"
              disabled={employeeSelection === 'individual' && selectedEmployee && !useCustomRate}
            />
            <span className="rate-unit">
              {paymentMethod === 'per_medication' ? 'per medication' : 'per hour'}
            </span>
          </div>
        </div>

        <div className="form-section">
          <h4>Export Format</h4>
          <select
            value={format}
            onChange={(e) => setFormat(e.target.value)}
          >
            <option value="csv">CSV (QuickBooks Desktop)</option>
            <option value="iif">IIF (QuickBooks Desktop Import)</option>
          </select>
        </div>

        <div className="form-section">
          <button
            className="btn-primary btn-large"
            onClick={handleExport}
            disabled={loading || !dateRange.startDate || !dateRange.endDate}
          >
            {loading ? 'Generating...' : 'Generate Payroll Report'}
          </button>
        </div>

        {dateRange.startDate && dateRange.endDate && (
          <div className="preview-info">
            <strong>Preview:</strong> Exporting payroll for {dateRange.startDate} to {dateRange.endDate}
            <br />
            Payment: {paymentMethod === 'per_medication' ? `$${rate} per medication` : `$${rate} per hour`}
          </div>
        )}
      </div>
    </div>
  );
}

export default PayrollExport;
